'use client';

import { useEffect, useMemo } from 'react';
import useMarketsForDlobToTrack, {
	MarketStatePriority,
} from '../useMarketsForDlobToTrack';
import useDriftStore from '../../stores/DriftStore/useDriftStore';
import { useUpdateAccountLoaderCadence } from '../drift-actions/connection/useUpdateAccountLoaderCadence';
import { MarketId } from '@drift/common';
import {
	PerpMarketAccount,
	SpotMarketAccount,
	CustomizedCadenceBulkAccountLoader,
} from '@drift-labs/sdk';
import Env from '../../environmentVariables/EnvironmentVariables';

const SELECTED_MARKET_CADENCE = Env.selectedMarketPollingCadenceMs ?? 1000;
const ACTIVE_MARKET_CADENCE = Env.activeMarketPollingCadenceMs ?? 1000;
const BACKGROUND_MARKET_CADENCE =
	Env.backgroundMarketPollingCadenceMs ?? 60_000;

const useSyncAccountLoaderCadenceWithMarketSubs = () => {
	const marketsToTrack = useMarketsForDlobToTrack();
	const driftClient = useDriftStore((s) => s.driftClient.client);
	const accountLoader = useDriftStore((s) => s.connection.accountLoader);
	const { updateAccountLoaderCadence } = useUpdateAccountLoaderCadence();

	const updateCadenceForMarket = (marketId: MarketId, cadence: number) => {
		if (!driftClient?.isSubscribed) {
			return;
		}

		const marketAccount = marketId.isPerp
			? driftClient?.getPerpMarketAccount(marketId.marketIndex)
			: driftClient?.getSpotMarketAccount(marketId.marketIndex);

		if (
			!marketAccount ||
			!accountLoader ||
			!(accountLoader instanceof CustomizedCadenceBulkAccountLoader)
		)
			return;

		// Update market account cadence
		const currentMarketCadence = accountLoader.getAccountCadence(
			marketAccount.pubkey
		);

		if (currentMarketCadence !== cadence) {
			updateAccountLoaderCadence(marketAccount.pubkey, cadence);
		}

		// Update oracle account cadence
		const oraclePublicKey = (() => {
			if (marketId.isPerp) {
				return (marketAccount as PerpMarketAccount).amm.oracle;
			}

			return (marketAccount as SpotMarketAccount).oracle;
		})();

		const currentOracleCadence =
			accountLoader.getAccountCadence(oraclePublicKey);

		if (currentOracleCadence !== cadence) {
			updateAccountLoaderCadence(oraclePublicKey, cadence);
		}
	};

	// Get markets from each category
	const selectedMarkets = useMemo(
		() => marketsToTrack.categories[MarketStatePriority.SelectedMarket] || [],
		[marketsToTrack.categories[MarketStatePriority.SelectedMarket]]
	);
	const deepMarkets = useMemo(
		() => marketsToTrack.categories[MarketStatePriority.BackgroundDeep] || [],
		[marketsToTrack.categories[MarketStatePriority.BackgroundDeep]]
	);
	const shallowMarkets = useMemo(
		() =>
			marketsToTrack.categories[MarketStatePriority.BackgroundShallow] || [],
		[marketsToTrack.categories[MarketStatePriority.BackgroundShallow]]
	);

	// Handle selected markets
	useEffect(() => {
		if (!driftClient || !accountLoader) return;

		selectedMarkets.forEach((marketId) => {
			updateCadenceForMarket(marketId, SELECTED_MARKET_CADENCE);
		});
	}, [driftClient, accountLoader, selectedMarkets]);

	// Handle deep markets
	useEffect(() => {
		if (!driftClient || !accountLoader) return;

		deepMarkets.forEach((marketId) => {
			updateCadenceForMarket(marketId, ACTIVE_MARKET_CADENCE);
		});
	}, [driftClient, accountLoader, deepMarkets]);

	// Handle shallow markets
	useEffect(() => {
		if (!driftClient || !accountLoader) return;

		shallowMarkets.forEach((marketId) => {
			updateCadenceForMarket(marketId, BACKGROUND_MARKET_CADENCE);
		});
	}, [driftClient, accountLoader, shallowMarkets]);
};

export default useSyncAccountLoaderCadenceWithMarketSubs;
